import { CSSProperties, memo, useCallback } from 'react'

import { FileIconSizes, FolderBlue } from '@inno-ui/file-icons'
import { toast } from '@inno-ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import { DEFAULT_SHORTCUT_ITEM_SIZE } from 'modules/desktop/constants'
import { pendingItemPositions } from 'modules/desktop/pages/MainPage/components/DesktopGrid/utils/pendingItemPositions'
import { useDesktopStore } from 'modules/desktop/pages/MainPage/stores'
import { useTranslation } from 'react-i18next'

import { useCreateFolderMutation } from '../../api/hooks/useCreateFolder'
import { useNewFolderPlaceholderStore } from '../../store/newFolderPlaceholderStore'
import { resolveUniqueFolderName, validateItemName } from '../../utils'
import { FolderViewItemInfo } from '../GridViewItem/components/Info'
import { InlineNameInput } from '../InlineNameInput'
import styles from './NewFolderPlaceholder.module.scss'

export const NewFolderPlaceholder = memo(() => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.context_menu'
  })

  const position = useNewFolderPlaceholderStore(state => state.position)
  const close = useNewFolderPlaceholderStore(state => state.close)

  const desktopItems = useDesktopStore(state => state.items)

  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useCreateFolderMutation()

  const handleSubmit = useCallback(
    (name: string) => {
      if (isPending) return

      if (!name) {
        close()
        return
      }

      const invalidName = validateItemName(name)
      if (invalidName) {
        toast.dismiss()
        toast.error({ ...invalidName })
        return
      }

      if (!position) return

      const existingLabels = desktopItems.map(item => item.label)
      const uniqueName = resolveUniqueFolderName(name, existingLabels)

      const placeholderPosition = position
      const errorState: { title: string; subTitle?: string } = {
        title: t('new_folder.failed')
      }

      const promise = mutateAsync({ name: uniqueName })
        .then(folder => {
          // Register position before refetch so the folder lands where the
          // placeholder was, not top-left.
          pendingItemPositions.set(folder.id, placeholderPosition)
          close()
          queryClient.invalidateQueries({ queryKey: ['files-list'] })
        })
        .catch(error => {
          if (error instanceof Error && error.message) {
            errorState.subTitle = error.message
          }
          throw error
        })

      toast.info({
        asyncHandler: promise,
        asyncStates: {
          loading: { title: t('new_folder.creating') },
          success: { title: t('new_folder.created') },
          error: errorState
        }
      })
    },
    [close, desktopItems, isPending, mutateAsync, position, queryClient, t]
  )

  const handleCancel = useCallback(() => {
    if (isPending) return
    close()
  }, [close, isPending])

  if (!position) {
    return null
  }

  const gridPlacement: CSSProperties = {
    gridColumnStart: position.col + 1,
    gridRowStart: position.row + 1,
    gridColumnEnd: `span ${DEFAULT_SHORTCUT_ITEM_SIZE.width}`,
    gridRowEnd: `span ${DEFAULT_SHORTCUT_ITEM_SIZE.height}`
  }

  return (
    <div className={styles.container} style={gridPlacement}>
      <div className={styles.iconWrapper}>
        <FolderBlue size={FileIconSizes.Size48} className='itemIcon' />
      </div>
      <div className={styles.wrapper}>
        <div className={styles.nameSlot}>
          <InlineNameInput
            initialValue={t('new_folder.default_name')}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </div>
        <FolderViewItemInfo />
      </div>
    </div>
  )
})
